import React, { useEffect, useState } from "react";
import { Users, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Team() {
  const { user, loadMe } = useAuth();
  const isAdmin = user?.workspace_role === "owner" || user?.workspace_role === "admin";
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [canMutate, setCanMutate] = useState(false);
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const [m, r] = await Promise.all([
        api.get("/workspace/members"),
        api.get("/workspace/action-requests"),
      ]);
      setMembers(m.data.members || []);
      setRequests(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      await api.post("/workspace/members", { email: email.trim(), role, can_mutate: canMutate });
      toast.success("Member added");
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add member");
    } finally {
      setInviting(false);
    }
  };

  const setGrant = async (m, allowed) => {
    try {
      await api.patch(`/workspace/members/${m.id}`, { can_mutate: allowed });
      toast.success(allowed ? "They can change workspace data via EMIE" : "Change permission removed");
      load();
      loadMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update");
    }
  };

  const decide = async (id, action) => {
    try {
      await api.post(`/workspace/action-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Change applied" : "Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update request");
    }
  };

  const pending = requests.filter((x) => x.status === "pending");

  return (
    <div data-testid="team-page">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Workspace</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Team</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Members can ask EMIE to create, update, or delete campaigns, jobs, and rules only if you grant it.
          Keys, subscription dates, and billing stay with platform superadmin — even on a white-label server.
        </p>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {isAdmin && (
            <div className="mb-8 rounded-2xl border border-border bg-card p-5" data-testid="team-invite">
              <h2 className="font-display text-lg font-bold">Add someone</h2>
              <p className="mt-1 text-sm text-muted-foreground">They must already have a GovernX login.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5 lg:col-span-2">
                  <Label>Work email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" data-testid="team-invite-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end justify-between gap-3 rounded-lg border border-border px-3 py-2">
                  <Label htmlFor="invite-mutate" className="text-sm">Can change data</Label>
                  <Switch id="invite-mutate" checked={role === "admin" || canMutate} onCheckedChange={setCanMutate} disabled={role === "admin"} />
                </div>
              </div>
              <Button className="mt-4 font-semibold" onClick={invite} disabled={inviting} data-testid="team-invite-submit">
                {inviting ? "Adding…" : "Add to workspace"}
              </Button>
            </div>
          )}

          {pending.length > 0 && (
            <div className="mb-8 rounded-2xl border border-primary/20 bg-primary/5 p-5" data-testid="team-pending">
              <h2 className="font-display text-lg font-bold">Waiting for approval</h2>
              <div className="mt-3 space-y-2">
                {pending.map((p) => (
                  <div key={p.id} className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm">
                      <span className="font-semibold">{p.requested_by}</span> wants <span className="font-semibold">{p.tool}</span>
                    </p>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button size="sm" className="gap-1" onClick={() => decide(p.id, "approve")} data-testid={`approve-${p.id}`}>
                          <Check className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => decide(p.id, "reject")}>
                          <X className="h-3.5 w-3.5" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg font-bold">People</h2>
            </div>
            <div className="space-y-2" data-testid="team-members">
              {members.map((m) => (
                <div key={m.user_id} className="flex flex-col gap-2 rounded-xl border border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold">{m.name || m.email}</p>
                    <p className="text-xs text-muted-foreground">{m.email} · {m.role}</p>
                  </div>
                  {isAdmin && m.role !== "owner" ? (
                    <label className="flex items-center gap-2 text-sm">
                      Can create / update / delete
                      <Switch checked={!!m.can_mutate} onCheckedChange={(v) => setGrant(m, v)} />
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground">{m.can_mutate ? "Can change data" : "Read and ask only"}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
